import { storeToRefs } from 'pinia';
import { unref, watch } from 'vue';
import type { Router } from 'vue-router';

import pinia from '@/plugins/pinia';
import { awsAuth } from '@/services/aws-auth.service';
import { useAuthStore } from '@/stores/auth';

// 定义不需要登录就可以访问的路径前缀
const publicPathPrefixes = [
  '/auth/', // 所有认证相关页面
  '/error/', // 错误页面
  '/layout/', // 布局测试和开发工具页面
];

/**
 * 全局导航守卫，用于检查用户是否已登录
 * 如果未登录且不是公共页面，则重定向到登录页
 */
export function authGuard(router: Router) {
  const authStore = useAuthStore(pinia);
  const { isLogin, loading } = storeToRefs(authStore);

  router.beforeEach(async (to, _from) => {
    // Wait for any pending authentication initialization to complete
    // This prevents race conditions during page refresh
    if (unref(loading)) {
      await new Promise<void>(resolve => {
        const unwatch = watch(loading, isLoading => {
          if (!isLoading) {
            unwatch();
            resolve();
          }
        });
      });
    }

    // For AWS authentication mode
    // Check if user has valid session
    const isAuthenticated = await awsAuth.isAuthenticated();

    // If authenticated but store is not initialized, load user data
    if (isAuthenticated && !unref(isLogin)) {
      await authStore.loadUserFromToken();

      // Load user organizations after successful token load
      const { useOrganizationStore } = await import('@/stores/organization');
      const organizationStore = useOrganizationStore();
      await organizationStore.loadUserOrganizations();
    }

    // Regular authentication check for production
    // 检查用户是否已登录
    const loggedIn = unref(isLogin) || isAuthenticated;

    // 如果用户未登录
    if (!loggedIn) {
      // 检查当前路径是否为公共路径（不需要登录）
      const isPublicPath = publicPathPrefixes.some(prefix => to.path.startsWith(prefix));

      // 如果不是公共路径且不是登录页，则重定向到登录页
      if (!isPublicPath && to.path !== '/auth/sign-in') {
        return { name: '/auth/sign-in' };
      }
    }

    // 如果路由明确要求认证且用户未登录
    if (to.meta.auth === true && !loggedIn && to.path !== '/auth/sign-in') {
      return { name: '/auth/sign-in' };
    }

    // Handle auth callback page
    if (to.path === '/auth/callback' && isAuthenticated) {
      // After successful authentication, redirect to home or last visited page
      const redirectPath = sessionStorage.getItem('authRedirect') || '/';
      sessionStorage.removeItem('authRedirect');
      return { path: redirectPath };
    }
  });
}
